/* eslint-disable */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { readPrerenderData, publishPrerenderData } from '../utils/prerenderData';

/**
 * InstallCounter — fetches install count from the existing HARTOS
 * `/api/social/marketing/stats` endpoint and renders a small social-
 * proof badge.  Endpoint aggregates a JSONL log of click/download/
 * install/signup events; we sum `install` across all referral codes.
 *
 * Graceful degrade:
 *   * Loading → render nothing (don't show a 0).
 *   * Error    → render nothing.
 *   * Count 0  → show "Be one of the first" friendly fallback.
 *   * Count <50 → show "X early users" with the real number.
 *   * Count >=50 → show "X+ people using Nunba" (rounded down to nearest 10).
 *
 * Self-contained, no Redux / context dependencies.  Drop into any page.
 *
 * Props:
 *   variant?: 'compact' | 'hero'  (default 'compact')
 *   className?: string            (optional Tailwind override)
 */
const API_BASE_URL =
  process.env.REACT_APP_SOCIAL_API_BASE_URL ||
  process.env.REACT_APP_API_BASE_URL ||
  '';

export default function InstallCounter({ variant = 'compact', className = '' }) {
  // Seeded from what the prerenderer fetched, so this renders the same thing on
  // the first pass as the saved HTML holds. Without it /download's file shows a
  // number and the client's first render shows "loading", which hydration
  // cannot reconcile: the page the whole download funnel ends at was still
  // reporting #418/#422 on production after its own attribution fix, because
  // this component is the other half of the mismatch.
  // See src/utils/prerenderData.js.
  const seed = readPrerenderData('installCount');
  const [count, setCount] = useState(seed ? seed.count : null);
  const [state, setState] = useState(seed ? seed.state : 'loading'); // loading | ready | error

  useEffect(() => {
    let cancelled = false;
    async function fetchStats() {
      try {
        const res = await axios.get(`${API_BASE_URL}/api/social/marketing/stats`, {
          timeout: 4000,
        });
        if (cancelled) return;
        const data = res?.data?.data || res?.data || {};
        const byCode = data.by_code || {};
        // Sum install events across all codes.
        let total = 0;
        for (const code of Object.keys(byCode)) {
          total += (byCode[code]?.install || 0);
        }
        setCount(total);
        setState('ready');
        publishPrerenderData('installCount', { count: total, state: 'ready' });
      } catch {
        if (!cancelled) setState('error');
        // Published on failure too, so a prerender whose fetch failed is
        // reproduced by the client rather than disagreed with.
        publishPrerenderData('installCount', { count: null, state: 'error' });
      }
    }
    fetchStats();
    return () => {
      cancelled = true;
    };
  }, []);

  if (state !== 'ready' || count === null) return null;

  // Choose copy based on magnitude.
  let copy;
  if (count === 0) {
    copy = 'Be one of the first to use Nunba';
  } else if (count < 50) {
    copy = `${count} early user${count === 1 ? '' : 's'} on Nunba`;
  } else {
    // Round down to nearest 10 for honest social proof.
    const rounded = Math.floor(count / 10) * 10;
    copy = `${rounded.toLocaleString()}+ people using Nunba`;
  }

  if (variant === 'hero') {
    return (
      <div
        className={className || 'inline-flex items-center px-4 py-2 rounded-full bg-emerald-600/20 border border-emerald-600 text-emerald-300 text-sm font-semibold'}
        aria-live="polite"
      >
        <span className="w-2 h-2 rounded-full bg-emerald-400 mr-2 animate-pulse" aria-hidden="true" />
        {copy}
      </div>
    );
  }

  // compact
  return (
    <span
      className={className || 'text-xs text-emerald-400 font-medium'}
      aria-live="polite"
    >
      {copy}
    </span>
  );
}
